// race-keirin-admin と race-keirin-frontend で同一の実装。変更は両リポジトリへ反映する
import { ApolloLink } from '@apollo/client';
import {
  FragmentDefinitionNode,
  getNamedType,
  GraphQLInputType,
  GraphQLObjectType,
  GraphQLOutputType,
  GraphQLSchema,
  GraphQLType,
  isAbstractType,
  isInputObjectType,
  isInputType,
  isListType,
  isNonNullType,
  isObjectType,
  isScalarType,
  Kind,
  OperationDefinitionNode,
  SelectionSetNode,
  typeFromAST,
} from 'graphql';
import { map } from 'rxjs';

/** カスタムスカラー1種の変換関数 */
export type ScalarFunctions = {
  /** クライアントの値 → リクエストへ送る値 */
  serialize: (value: unknown) => unknown;
  /** レスポンスの値 → クライアントの値 */
  parseValue: (raw: unknown) => unknown;
};

/** スカラー名 → 変換関数 */
export type ScalarsMap = Record<string, ScalarFunctions>;

type Fragments = Record<string, FragmentDefinitionNode>;

/** 同一のレスポンスキーに対する選択セットを集約したもの（エイリアス・フラグメントによる重複を1回で変換するため） */
type FieldGroup = {
  type: GraphQLOutputType;
  selectionSets: SelectionSetNode[];
};

const unwrapNonNull = <T extends GraphQLType>(type: T): T =>
  (isNonNullType(type) ? type.ofType : type) as T;

const serializeInput = (
  type: GraphQLInputType,
  value: unknown,
  scalars: ScalarsMap,
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }
  const nullable = unwrapNonNull(type);
  if (isListType(nullable)) {
    return Array.isArray(value)
      ? value.map(v => serializeInput(nullable.ofType, v, scalars))
      : serializeInput(nullable.ofType, value, scalars);
  }
  if (isScalarType(nullable)) {
    const fn = scalars[nullable.name];
    return fn ? fn.serialize(value) : value;
  }
  if (isInputObjectType(nullable) && typeof value === 'object') {
    const fields = nullable.getFields();
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        fields[k] ? serializeInput(fields[k].type, v, scalars) : v,
      ]),
    );
  }
  return value;
};

/** 選択セットを走査し、レスポンスキーごとにフィールド定義と選択セットを集める */
const collectFields = (
  selectionSet: SelectionSetNode,
  objectType: GraphQLObjectType,
  schema: GraphQLSchema,
  fragments: Fragments,
  groups: Map<string, FieldGroup>,
) => {
  selectionSet.selections.forEach(selection => {
    if (selection.kind === Kind.FIELD) {
      const def = objectType.getFields()[selection.name.value];
      if (!def) {
        return; // __typename 等
      }
      const key = selection.alias?.value ?? selection.name.value;
      const group = groups.get(key) ?? { type: def.type, selectionSets: [] };
      if (selection.selectionSet) {
        group.selectionSets.push(selection.selectionSet);
      }
      groups.set(key, group);
      return;
    }
    const conditionName =
      selection.kind === Kind.INLINE_FRAGMENT
        ? selection.typeCondition?.name.value
        : fragments[selection.name.value]?.typeCondition.name.value;
    const body =
      selection.kind === Kind.INLINE_FRAGMENT
        ? selection.selectionSet
        : fragments[selection.name.value]?.selectionSet;
    if (!body) {
      return;
    }
    const condition = conditionName ? schema.getType(conditionName) : undefined;
    const applies =
      !condition ||
      condition === objectType ||
      (isAbstractType(condition) && schema.isSubType(condition, objectType));
    if (applies) {
      collectFields(body, objectType, schema, fragments, groups);
    }
  });
};

/** 開発時の警告済み（選択セット → 抽象型名）。同一の選択セット・型で警告を繰り返さない */
const warnedAbstract = new WeakMap<SelectionSetNode, Set<string>>();

/** 抽象型で __typename が無く具象型を決められない（変換されない）ことを開発時のみ警告する */
const warnUnresolvedAbstract = (
  typeName: string,
  selectionSets: SelectionSetNode[],
) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const [first] = selectionSets;
  const warned = warnedAbstract.get(first) ?? new Set<string>();
  if (warned.has(typeName)) {
    return;
  }
  warned.add(typeName);
  warnedAbstract.set(first, warned);
  console.warn(
    `[scalarLink] 抽象型 ${typeName} の値に __typename が無い、またはスキーマに存在しない型のため具象型を決められず、カスタムスカラーを変換しません。選択セットに __typename を含めてください`,
  );
};

const parseOutput = (
  type: GraphQLOutputType,
  selectionSets: SelectionSetNode[],
  value: unknown,
  ctx: { schema: GraphQLSchema; fragments: Fragments; scalars: ScalarsMap },
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }
  const nullable = unwrapNonNull(type);
  if (isListType(nullable)) {
    return Array.isArray(value)
      ? value.map(v => parseOutput(nullable.ofType, selectionSets, v, ctx))
      : value;
  }
  const named = getNamedType(nullable);
  if (isScalarType(named)) {
    const fn = ctx.scalars[named.name];
    return fn ? fn.parseValue(value) : value;
  }
  if (typeof value !== 'object' || selectionSets.length === 0) {
    return value;
  }
  // 抽象型（interface/union）は __typename から具象型を決める
  const concrete = isObjectType(named)
    ? named
    : (() => {
        const t = ctx.schema.getType(
          (value as Record<string, unknown>).__typename as string,
        );
        return t && isObjectType(t) ? t : undefined;
      })();
  if (!concrete) {
    warnUnresolvedAbstract(named.name, selectionSets);
    return value;
  }
  const groups = new Map<string, FieldGroup>();
  selectionSets.forEach(s =>
    collectFields(s, concrete, ctx.schema, ctx.fragments, groups),
  );
  const result: Record<string, unknown> = {
    ...(value as Record<string, unknown>),
  };
  groups.forEach((group, key) => {
    if (key in result) {
      result[key] = parseOutput(
        group.type,
        group.selectionSets,
        result[key],
        ctx,
      );
    }
  });
  return result;
};

/**
 * レスポンスとリクエスト変数のカスタムスカラーを、スキーマに基づいて変換するApolloLink。
 *
 * - リクエスト: 変数定義の型に従い、カスタムスカラーの値（入力オブジェクト・リスト内を含む）をserializeする
 * - レスポンス: 選択セットとスキーマに従い、カスタムスカラーのフィールドをparseValueする
 *   （エイリアス・フラグメント・interface/union・nullに対応）
 */
export const createScalarLink = (schema: GraphQLSchema, scalars: ScalarsMap) =>
  new ApolloLink((operation, forward) => {
    const fragments: Fragments = {};
    let definition: OperationDefinitionNode | undefined;
    operation.query.definitions.forEach(def => {
      if (def.kind === Kind.FRAGMENT_DEFINITION) {
        fragments[def.name.value] = def;
      } else if (
        def.kind === Kind.OPERATION_DEFINITION &&
        (!definition ||
          (operation.operationName &&
            def.name?.value === operation.operationName))
      ) {
        definition = def;
      }
    });
    if (!definition) {
      return forward(operation);
    }

    const variables = operation.variables ?? {};
    operation.variables = Object.fromEntries(
      Object.entries(variables).map(([name, value]) => {
        const varDef = definition?.variableDefinitions?.find(
          v => v.variable.name.value === name,
        );
        const varType = varDef ? typeFromAST(schema, varDef.type) : undefined;
        return [
          name,
          varType && isInputType(varType)
            ? serializeInput(varType, value, scalars)
            : value,
        ];
      }),
    );

    const rootType =
      definition.operation === 'mutation'
        ? schema.getMutationType()
        : definition.operation === 'subscription'
          ? schema.getSubscriptionType()
          : schema.getQueryType();
    const selectionSet = definition.selectionSet;

    return forward(operation).pipe(
      map(result => {
        if (!rootType || !result.data) {
          return result;
        }
        return {
          ...result,
          data: parseOutput(rootType, [selectionSet], result.data, {
            schema,
            fragments,
            scalars,
          }) as typeof result.data,
        };
      }),
    );
  });
